import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { decode } from 'jsonwebtoken';
import { PAGES } from '../constants/pages';
import useApplicationUid from './useApplicationUid';
import {
  applicationTypeSetup,
  createApplication,
  getApplication,
} from '../store/actions/application/applicationActions';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE,
  APPLICATION_TYPE,
  BASIC_ACCOUNT_QUERY_DATA,
  IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  IS_UPGRADE_FLOW,
  NEW_APPLICATION,
  REGISTRATION_FLOW_TYPE,
  SALES_ASSISTED_QUERY_DATA,
} from '../constants/sessionStorage';
import { createUser, refreshTokens } from '../store/actions/user/userActions';
import useTrackingDetails from './useTrackingDetails';
import {
  BASIC_ACCOUNT_UPGRADE_TYPES,
  CREATE_ACCOUNT_PAGE,
  CREATE_NEW_APPLICATION,
  CURRENT_PATH,
  REGISTRATION_FLOW_TYPES,
} from '../util/constants';
import { selectBasicAccount } from '../store/selectors/userSelectors';
import { JWT_TOKEN } from '../constants/localStorage';
import { APPLICATION_STARTED } from '../util/gtmEvents';
import useGtmHook from '../util/hooks/useGtmHook';
import { selectApplication } from '../store/selectors/applicationSelectors';
import { getTokens } from '../util/helpers/getTokensHelper';
import { isAipRegistrationFlow } from '../util/helpers/registrationFlowTypeHelper';

const useChooseApplication = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { gtmEvent } = useGtmHook();
  const { ApplicationUid } = useApplicationUid();
  const { TrackingDetails } = useTrackingDetails();
  const basicAccountQueryData = retrieveFromSessionStorage(
    BASIC_ACCOUNT_QUERY_DATA,
  );
  const isCreateNewApplication = retrieveFromSessionStorage(
    CREATE_NEW_APPLICATION,
  );
  const basicAccountData = useSelector(selectBasicAccount);
  const isUpgradeFlow = retrieveFromSessionStorage(IS_UPGRADE_FLOW);
  const Application = useSelector(selectApplication);
  const { AccountUpgradeType } = Application;
  const isUpgrade =
    AccountUpgradeType === BASIC_ACCOUNT_UPGRADE_TYPES.IN_PROCESS;
  const registrationFlowType = retrieveFromSessionStorage(
    REGISTRATION_FLOW_TYPE,
  );
  const isAipFlow = isAipRegistrationFlow(registrationFlowType);
  const { JwtToken, JwtRefreshToken } = getTokens();
  const isSalesAssistedRegistration = retrieveFromSessionStorage(
    IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  );
  const salesAssistedData = retrieveFromSessionStorage(
    SALES_ASSISTED_QUERY_DATA,
  );

  const startNewApplication = (data) => {
    storeInSessionStorage(CREATE_ACCOUNT_PAGE, true);
    const onSuccess = () => {
      dispatch(
        refreshTokens({
          data: {
            JwtToken,
            JwtRefreshToken,
          },
          onSuccess: () => {
            history.push(PAGES.LOADING);
            removeFromSessionStorage(CREATE_NEW_APPLICATION);
            document.body.style.overflow = 'unset';
          },
        }),
      );
    };
    const { ApplicationUid } = decode(JwtToken);
    const params = {
      registrationFlowType: isAipFlow
        ? REGISTRATION_FLOW_TYPES.AIP
        : REGISTRATION_FLOW_TYPES.REGULAR,
    };
    dispatch(
      createApplication({
        data,
        onSuccess,
        ApplicationUid,
        params,
      }),
    );
  };
  const onSelectType = (data) => {
    if (!data) {
      return;
    }
    const currentPath = history.location.pathname;
    storeInSessionStorage(CURRENT_PATH, currentPath);
    const isCreateAccount = retrieveFromSessionStorage(CREATE_ACCOUNT_PAGE);

    if (
      isCreateNewApplication ||
      (isCreateAccount &&
        !isUpgrade &&
        !isUpgradeFlow &&
        (currentPath === PAGES.CHOOSE_APPLICATION.STANDARD.MAIN ||
          currentPath === PAGES.CHOOSE_APPLICATION.UTMA.MAIN ||
          currentPath === PAGES.CHOOSE_APPLICATION.RETIREMENT.MAIN ||
          currentPath === PAGES.CHOOSE_APPLICATION.RETIREMENT.PERSONAL ||
          currentPath === PAGES.CHOOSE_APPLICATION.RETIREMENT.BUSINESS))
    ) {
      startNewApplication(data);
      return;
    }

    storeInSessionStorage(
      APPLICATION_TYPE,
      `${data.ApplicationType}Application`,
    );

    if (data.CorporateAccountType) {
      storeInSessionStorage(ACCOUNT_TYPE, data?.CorporateAccountType);
    }
    if (data.UTMAAccountType) {
      storeInSessionStorage(ACCOUNT_TYPE, data?.UTMAAccountType);
    }
    if (data.IRAAccountType) {
      storeInSessionStorage(ACCOUNT_TYPE, data?.IRAAccountType);
    }

    const onCreateUserSuccess = () => {
      gtmEvent(APPLICATION_STARTED);

      const JwtToken = retrieveFromSessionStorage(JWT_TOKEN);
      if (!JwtToken) {
        return;
      }

      const onGetApplicationSuccess = (application) => {
        history.push({
          pathname: PAGES.LOADING,
          state: { application },
        });
      };
      const { ApplicationUid } = decode(JwtToken);
      if (ApplicationUid !== '00000000-0000-0000-0000-000000000000') {
        dispatch(
          getApplication({
            data: { ApplicationUid },
            onSuccess: onGetApplicationSuccess,
          }),
        );
      }
    };

    if (isSalesAssistedRegistration) {
      const { LogonUid, PortalLogonUid } = decode(JwtToken);
      if (PortalLogonUid) {
        dispatch(
          createUser({
            data: {
              type: data,
              TrackingDetails,
              applicationType: data.ApplicationType,
              accountType: retrieveFromSessionStorage(ACCOUNT_TYPE),
              registrationFlowType: REGISTRATION_FLOW_TYPES.REGULAR,
              Email: salesAssistedData?.customerEmail,
              logonUid: LogonUid,
              isUpgrade: false,
              userUid: PortalLogonUid,
            },
            onSuccess: onCreateUserSuccess,
            onError: () => console.log(error), //eslint-disable-line
          }),
        );
      } else {
        dispatch(
          createUser({
            data: {
              type: data,
              TrackingDetails,
              applicationType: data.ApplicationType,
              accountType: retrieveFromSessionStorage(ACCOUNT_TYPE),
              registrationFlowType: REGISTRATION_FLOW_TYPES.REGULAR,
              Email: salesAssistedData?.customerEmail,
              logonUid: LogonUid,
              isUpgrade: false,
            },
            onSuccess: onCreateUserSuccess,
            onError: () => console.log(error), //eslint-disable-line
          }),
        );
      }
      return;
    }

    if (isUpgrade || isUpgradeFlow) {
      storeInSessionStorage(CREATE_ACCOUNT_PAGE, true);
      const { PortalLogonUid } = decode(JwtToken);
      dispatch(
        createUser({
          data: {
            type: data,
            TrackingDetails,
            applicationType: data.ApplicationType,
            accountType: retrieveFromSessionStorage(ACCOUNT_TYPE),
            registrationFlowType: REGISTRATION_FLOW_TYPES.REGULAR,
            Email: basicAccountData?.Email && basicAccountData?.Email,
            accountUid:
              basicAccountQueryData?.accountUid || basicAccountData?.AccountUid,
            accountCode:
              basicAccountQueryData?.accountCode ||
              basicAccountData?.AccountCode,
            userUid: PortalLogonUid,
            isUpgrade: isUpgrade || isUpgradeFlow,
          },
          onSuccess: onCreateUserSuccess,
          onError: () => console.log(error), //eslint-disable-line
        }),
      );

      return;
    }

    const onSuccess = () => {
      dispatch(
        getApplication({
          data: { ApplicationUid },
          onSuccess: () => {
            removeFromSessionStorage(NEW_APPLICATION);
            history.push(PAGES.LOADING);
          },
        }),
      );
    };

    dispatch(
      applicationTypeSetup({
        data,
        onSuccess,
        ApplicationUid,
      }),
    );
  };

  return { onSelectType };
};

useChooseApplication.propTypes = {};

export default useChooseApplication;
